import { isAxiosError } from 'axios';
import { apiClient } from '@/api/api-client';
import type { InitialDataMode } from './types';

/** Where the contributor stopped in a session, as the backend stores it. */
export interface SavedSessionPosition {
  mode: InitialDataMode;
  language: string;
  sectorId: string;
  currentQuestionId: string | null;
  skippedQuestionIds: string[];
  answeredCount: number | null;
  totalCount: number | null;
  updatedAt: string;
}

export type SessionPositionInput = Omit<SavedSessionPosition, 'mode' | 'updatedAt'>;

/**
 * Local copy of the position. `synced: false` means the backend has not seen
 * the latest change yet (offline, closed tab, older backend); `position: null`
 * then stands for a pending "forget it".
 */
type LocalEntry = { position: SavedSessionPosition | null; synced: boolean };

const localKey = (userId: string, mode: InitialDataMode) => `umbaji:initial-data-position:${userId}:${mode}`;
const endpoint = (mode: InitialDataMode) => `/progress/initial-data/${mode}`;

// Set once the backend answers 404/405: it predates the endpoint, so this tab
// keeps the position in localStorage only.
let remoteUnavailable = false;

function isEndpointMissing(error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status === 404 || status === 405;
}

function toPosition(value: unknown, mode: InitialDataMode): SavedSessionPosition | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.language !== 'string' || !raw.language || typeof raw.sectorId !== 'string' || !raw.sectorId) return null;
  const count = (input: unknown) => (typeof input === 'number' && Number.isFinite(input) ? input : null);
  return {
    mode,
    language: raw.language,
    sectorId: raw.sectorId,
    currentQuestionId: typeof raw.currentQuestionId === 'string' ? raw.currentQuestionId : null,
    skippedQuestionIds: Array.isArray(raw.skippedQuestionIds)
      ? raw.skippedQuestionIds.filter((id): id is string => typeof id === 'string')
      : [],
    answeredCount: count(raw.answeredCount),
    totalCount: count(raw.totalCount),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date(0).toISOString(),
  };
}

function readLocal(userId: string, mode: InitialDataMode): LocalEntry | null {
  try {
    const stored = window.localStorage.getItem(localKey(userId, mode));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<LocalEntry>;
    return { position: toPosition(parsed.position, mode), synced: parsed.synced === true };
  } catch {
    return null;
  }
}

function writeLocal(userId: string, mode: InitialDataMode, entry: LocalEntry): void {
  try {
    window.localStorage.setItem(localKey(userId, mode), JSON.stringify(entry));
  } catch {
    // Storage full or blocked: the backend copy is all there is.
  }
}

/** Marks the local copy as seen by the backend, unless a newer change replaced it meanwhile. */
function markSynced(userId: string, mode: InitialDataMode, sent: SavedSessionPosition | null): void {
  const local = readLocal(userId, mode);
  if (!local) return;
  if ((local.position?.updatedAt ?? null) !== (sent?.updatedAt ?? null)) return;
  writeLocal(userId, mode, { position: local.position, synced: true });
}

async function pushRemote(userId: string, mode: InitialDataMode, position: SavedSessionPosition | null): Promise<void> {
  if (remoteUnavailable) return;
  try {
    if (position) {
      const body: SessionPositionInput = {
        language: position.language,
        sectorId: position.sectorId,
        currentQuestionId: position.currentQuestionId,
        skippedQuestionIds: position.skippedQuestionIds,
        answeredCount: position.answeredCount,
        totalCount: position.totalCount,
      };
      // Unknown values are left out rather than sent as null.
      const payload = Object.fromEntries(Object.entries(body).filter(([, value]) => value !== null));
      await apiClient.put(endpoint(mode), payload);
    } else {
      await apiClient.delete(endpoint(mode));
    }
    markSynced(userId, mode, position);
  } catch (error) {
    if (isEndpointMissing(error)) remoteUnavailable = true;
  }
}

/**
 * The saved position for this user and mode. A local change the backend has
 * not received yet wins and is sent again; otherwise the backend is the source
 * of truth, and localStorage covers an older backend or a network failure.
 */
export async function loadSessionPosition(userId: string, mode: InitialDataMode): Promise<SavedSessionPosition | null> {
  const local = readLocal(userId, mode);
  if (remoteUnavailable) return local?.position ?? null;
  if (local && !local.synced) {
    void pushRemote(userId, mode, local.position);
    return local.position;
  }
  try {
    const { data } = await apiClient.get<{ position?: unknown }>(endpoint(mode));
    const remote = toPosition(data?.position, mode);
    writeLocal(userId, mode, { position: remote, synced: true });
    return remote;
  } catch (error) {
    if (isEndpointMissing(error)) remoteUnavailable = true;
    return local?.position ?? null;
  }
}

/** Keeps the position locally right away, then sends it to the backend. */
export function saveSessionPosition(userId: string, mode: InitialDataMode, input: SessionPositionInput): SavedSessionPosition {
  const position: SavedSessionPosition = { ...input, mode, updatedAt: new Date().toISOString() };
  writeLocal(userId, mode, { position, synced: false });
  void pushRemote(userId, mode, position);
  return position;
}

export function clearSessionPosition(userId: string, mode: InitialDataMode): void {
  writeLocal(userId, mode, { position: null, synced: false });
  void pushRemote(userId, mode, null);
}
